import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Membership, User, UserPreferences, UserProfile } from "./types";
import { INITIAL_STATE } from "./consts";
import { proxyClient } from "~/utils/api";
import { FeatureNames } from "~/modules/feature_flag/flags";

// // // //

/**
 * A Redux thunk that fetches the user profile data from the server via tRPC.
 * @returns {Promise<User>} A Promise that resolves with the user profile data.
 */
export const fetchUserProfile = createAsyncThunk(
    "user/fetchUserProfile",
    async () => {
        // NOTE - we add the `skipBatch` context to the request so the `getProfile` query is NOT batched with other requests
        // This improves response time for the user auth handshake
        // Doc: https://trpc.io/docs/v9/links#disable-batching-for-certain-requests
        const response = await proxyClient.users.getProfile.query(undefined, {
            context: {
                skipBatch: true,
            },
        });
        return response;
    },
);

/**
 * A Redux thunk that fetches the user's pending invitations from the server via tRPC.
 * @returns {Promise<Membership[]>} A Promise that resolves with the pending invitations.
 */
export const fetchInvitations = createAsyncThunk(
    "user/fetchInvitations",
    async () => {
        const response = await proxyClient.users.getInvitations.query();
        return response;
    },
);

/**
 * The user slice of the Redux store, created using the createSlice function from @reduxjs/toolkit.
 * @typedef {Object} UserSlice
 * @property {Object} initialState - The initial state of the user slice.
 * @property {Function} reducers - The reducer functions for the user slice.
 * @property {Function} extraReducers - The extra reducer functions for the user slice.
 */
export const userSlice = createSlice({
    name: "user",
    initialState: INITIAL_STATE,
    reducers: {
        syncUserProfile: (state, action: PayloadAction<UserProfile>) => {
            state.user.profile = {
                ...action.payload,
            };
        },
        syncUserPreferences: (
            state,
            action: PayloadAction<UserPreferences>,
        ) => {
            state.user.preferences = {
                ...action.payload,
            };
        },
        syncUserName: (state, action: PayloadAction<string>) => {
            state.user.name = action.payload;
        },
        togglePreviewFeatures: (state) => {
            // Only superadmins can toggle preview features
            if (state.user.role !== "SUPERADMIN") {
                return;
            }

            // Toggle the experimental features flag between true/false
            state.user.features[FeatureNames.enableExperimentalFeatures] =
                !state.user.features[FeatureNames.enableExperimentalFeatures];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.isLoggedIn = false;
            })
            .addCase(fetchUserProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload as unknown as User; // TODO - cleanup this type coercion
                state.isLoggedIn = true;
            })
            .addCase(fetchUserProfile.rejected, (state, action) => {
                state.loading = false;
                state.isLoggedIn = false;
                state.error =
                    action.error.message ??
                    "An error occurred while fetching user profile";
            })
            .addCase(fetchInvitations.pending, (state) => {
                state.user.pending_invitations = [];
            })
            .addCase(fetchInvitations.fulfilled, (state, action) => {
                state.user.pending_invitations =
                    action.payload as unknown as Membership[];
            });
    },
});

/**
 * The reducer function for the user slice of the Redux store.
 */
export const {
    syncUserProfile,
    syncUserPreferences,
    syncUserName,
    togglePreviewFeatures,
} = userSlice.actions;
export const reducer = userSlice.reducer;
